// Formatering av ULLs egne størrelser, og navn på kontraktens enum-verdier.
//
// HVORFOR EN EGEN FIL MED TESTER: her ligger den ene formatereren hele appen
// hviler på — `formaterFartsintervall`. Kontraktens `sekvensSpor()` returnerer
// fart som et INTERVALL, og et UI som viser midtverdien i stedet ville vært den
// samme løgnen som en peiling uten usikkerhet. «80 m på 20 s» ser ut som 4,00
// m/s, men med ±20 m og ±2 s er svaret 1,4–7,2 m/s, altså alt fra rolig gange til
// spurt. Bredden ER informasjonen: den sier at sensorene står for tett for å måle
// fart. Derfor står regelen ett sted, og den er testet.
//
// ══ HVA SOM ER HER, OG HVA SOM ER I `@skade/sensorflate` ════════════════════
// Pakken eier formaterere for størrelser som betyr det samme overalt: grader,
// avstand, frekvens, sikkerhet 0–1, alder, klokke. De brukes DIREKTE herfra, og
// gjentas ikke.
//
// Det som ligger her er:
//   * fart i METER PER SEKUND, og som INTERVALL. NJORDs `formaterFart` er i knop
//     (maritimt) og gir ett tall. Et fartsintervall i m/s er ikke maritimt og
//     ikke ULL-spesifikt — se rapporten: `formaterFartMs` og
//     `formaterFartsintervall` bør flyttes til sensorflate når en tredje flate
//     trenger dem.
//   * `himmelretning`, som finnes fordi Forsvarets stridsmelding krever
//     HIMMELRETNINGER i STORE BOKSTAVER (se `stridsmelding.ts`). Den er derfor
//     ikke pynt, men et doktrinekrav i meldingsutkastet.
//   * navn på kontraktens enum-verdier — ren UI-tekst, som `Record<…>` slik at en
//     ny verdi i kontrakten blir en byggefeil og ikke en tom celle.
import type {
  Eksponeringsrisiko, LenkeKvalitet, PosisjonsMetode, SkjultGrad,
  UgsKlassifisering, UgsLydklasse, UgsMalklasse,
} from '@skade/contracts';

/** Norsk desimaltall. Samme regel som i pakken: komma, ikke punktum. */
function nb(v: number, desimaler: number): string {
  return v.toFixed(desimaler).replace('.', ',');
}

// ── Fart ────────────────────────────────────────────────────────────────────

/**
 * Fart i m/s med ÉN desimal. Ikke to: den andre desimalen på et tall som er
 * utledet av en deteksjonsradius på ±20 m er ren pynt, og pynt på et usikkert
 * tall leses som presisjon.
 */
export function formaterFartMs(v: number): string {
  if (!Number.isFinite(v)) return '–';
  return `${nb(v, 1)} m/s`;
}

/** Fart omregnet til km/t, for den som tenker i marsjfart. */
export function formaterFartKmt(v: number): string {
  if (!Number.isFinite(v)) return '–';
  return `${nb(v * 3.6, 1)} km/t`;
}

export type Fartsutsagn =
  /** Både nedre og øvre grense finnes. Dette er normaltilfellet. */
  | { slag: 'intervall'; minMs: number; maksMs: number; tekst: string }
  /**
   * Øvre grense mangler fordi klokkeslarken er større enn tidsdifferansen. Da er
   * farten UBEGRENSET oppover, og det skal stå — ikke erstattes med et stort tall.
   */
  | { slag: 'nedre-grense'; minMs: number; tekst: string }
  /** Samme sensor to ganger, eller ingen tid: ingen fart finnes. */
  | { slag: 'ingen'; tekst: string };

/**
 * Fartsintervallet som utsagn. Diskriminert union og ikke bare en streng, fordi
 * de tre tilfellene skal TEGNES ulikt: et intervall får en stolpe, en nedre
 * grense får et «≥», og «ingen fart» skal ikke få noe tall i det hele tatt.
 */
export function fartsutsagn(minMs?: number, maksMs?: number): Fartsutsagn {
  if (minMs === undefined || !Number.isFinite(minMs)) {
    return {
      slag: 'ingen',
      tekst: 'Ingen fart kan regnes — avstanden mellom utløsningene er null (samme sensor)',
    };
  }
  if (maksMs === undefined || !Number.isFinite(maksMs)) {
    return {
      slag: 'nedre-grense',
      minMs,
      tekst: `≥ ${formaterFartMs(minMs)} — INGEN øvre grense: klokkeslarken er større enn tiden mellom utløsningene`,
    };
  }
  return {
    slag: 'intervall',
    minMs,
    maksMs,
    tekst: `${nb(minMs, 1)}–${nb(maksMs, 1)} m/s (${nb(minMs * 3.6, 1)}–${nb(maksMs * 3.6, 1)} km/t)`,
  };
}

/** Kort form av `fartsutsagn().tekst`, til lister der plassen er trang. */
export function formaterFartsintervall(minMs?: number, maksMs?: number): string {
  return fartsutsagn(minMs, maksMs).tekst;
}

// ── Avstand, tid og retning med slark ───────────────────────────────────────

/**
 * Avstand med slark: «210 m ±83 m». Slarken står ALLTID, også når den er liten —
 * et tall uten slark leses som målt, og ingen av avstandene i denne appen er
 * målt: de er avstander mellom SENSORER, og målet kan ha vært hvor som helst
 * innenfor deteksjonsradien.
 */
export function formaterAvstandSlakk(meter: number, slakkM: number): string {
  return `${Math.round(meter)} m ±${Math.round(slakkM)} m`;
}

/** Tid med slark: «150,0 s ±1,8 s». Samme regel som over. */
export function formaterSekunderSlakk(sekunder: number, slakkS: number): string {
  return `${nb(Math.abs(sekunder), 1)} s ±${nb(slakkS, 1)} s`;
}

/** Varighet i lesbar form. Sekunder under et minutt, ellers min/timer. */
export function formaterVarighet(sekunder: number): string {
  if (!Number.isFinite(sekunder)) return '–';
  const s = Math.abs(sekunder);
  if (s < 60) return `${nb(s, 1)} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`;
  return `${Math.floor(s / 3600)} t ${Math.round((s % 3600) / 60)} min`;
}

const RETNINGER = [
  'NORD', 'NORDØST', 'ØST', 'SØRØST', 'SØR', 'SØRVEST', 'VEST', 'NORDVEST',
] as const;

/**
 * Kurs → himmelretning med STORE bokstaver.
 *
 * Åtte sektorer og ikke seksten: sekvensanalysen gir kurs med titalls graders
 * slark, og «ØSTNORDØST» fra et tall som er ±22° er en presisjon som ikke finnes.
 * Store bokstaver fordi Forsvarets stridsmelding krever det (`stridsmelding.ts`),
 * og fordi utkastet i `lib/melding.ts` bruker samme funksjon — da kan ikke
 * skjermen og meldingen si ulike ting.
 */
export function himmelretning(kursDeg: number): string {
  if (!Number.isFinite(kursDeg)) return 'UKJENT RETNING';
  const n = ((kursDeg % 360) + 360) % 360;
  const i = Math.round(n / 45) % 8;
  return RETNINGER[i];
}

/** «070° ±22° (ØSTNORDØST)» — tall for kartet, ord for sambandet. */
export function formaterKursSlakk(kursDeg: number, slakkDeg?: number): string {
  const grader = String(Math.round(((kursDeg % 360) + 360) % 360)).padStart(3, '0');
  const slakk = slakkDeg === undefined ? '' : ` ±${Math.round(slakkDeg)}°`;
  return `${grader}°${slakk} mot ${himmelretning(kursDeg)}`;
}

/** «antatt 2 (mulig 2–4)» — aldri bare det antatte tallet. */
export function formaterAntall(antatt: number, min: number, maks: number): string {
  return min === maks ? `${antatt}` : `antatt ${antatt} (mulig ${min}–${maks})`;
}

// ── Navn på kontraktens enum-verdier ────────────────────────────────────────

export const MALKLASSE_NAVN: Record<UgsMalklasse, string> = {
  'person-til-fots': 'Person til fots',
  'person-lopende': 'Person i løp',
  hjul: 'Hjulkjøretøy',
  belte: 'Beltekjøretøy',
  dyr: 'Dyr',
  ukjent: 'Ukjent',
};

export const LYDKLASSE_NAVN: Record<UgsLydklasse, string> = {
  skritt: 'Skritt',
  stemme: 'Stemme',
  'kjøretøy-diesel': 'Kjøretøy (diesel)',
  'kjøretøy-bensin': 'Kjøretøy (bensin)',
  beltestøy: 'Beltestøy',
  rotor: 'Rotor',
  skudd: 'Skudd',
  ukjent: 'Ukjent lyd',
};

/**
 * Hva klassifiseringen bygger på. `menneske` står med store bokstaver fordi det
 * er kvalitativt noe annet enn resten: en person har SETT bildet, og det er den
 * eneste UGS-verdien som fortjener høy konfidens (`KONFIDENS_VURDERT_BILDE`).
 */
export const GRUNNLAG_NAVN: Record<UgsKlassifisering['grunnlag'], string> = {
  menneske: 'MENNESKE HAR SETT BILDET',
  skrittrate: 'Skrittrate (utledet periodisitet)',
  varighet: 'Varighet på utslaget',
  amplitude: 'Amplitude',
  lydklasse: 'Lydklasse fra noden',
  passeringsfart: 'Passeringsfart (dobbelstråle)',
  ingen: 'Ingen holdepunkt',
};

export const POSISJONSMETODE_NAVN: Record<PosisjonsMetode, string> = {
  gps: 'GPS på stedet',
  kartlagt: 'Kartlagt etter terrengholdepunkt',
  anslått: 'ANSLÅTT — omtrentlig',
};

export const SKJULT_NAVN: Record<SkjultGrad, string> = {
  'godt-skjult': 'Godt skjult',
  'delvis-skjult': 'Delvis skjult',
  synlig: 'SYNLIG',
};

export const LENKE_NAVN: Record<LenkeKvalitet, string> = {
  god: 'God lenke',
  marginal: 'Marginal lenke',
  tapt: 'LENKE TAPT',
  'ingen-lenke': 'Ingen lenke (logger lokalt)',
};

export const EKSPONERING_NAVN: Record<Eksponeringsrisiko, string> = {
  lav: 'Lav eksponeringsrisiko',
  middels: 'Middels eksponeringsrisiko',
  høy: 'HØY eksponeringsrisiko',
};

export const LYSFORHOLD_NAVN: Record<'dagslys' | 'skumring' | 'ir-blits' | 'natt-uten-lys', string> = {
  dagslys: 'Dagslys',
  skumring: 'Skumring',
  'ir-blits': 'IR-blits (mørkt)',
  'natt-uten-lys': 'Natt uten lys',
};

export const BILDEARSAK_NAVN: Record<'pir' | 'tidsintervall' | 'manuell' | 'bevegelse-i-bilde', string> = {
  pir: 'Utløst av PIR',
  tidsintervall: 'Tidsintervall (tok bilde uansett)',
  manuell: 'Manuelt utløst',
  'bevegelse-i-bilde': 'Bevegelse i bildet',
};

/**
 * Hva et TOMT bilde betyr, gitt hvorfor kameraet utløste.
 *
 * Dette er ikke pedanteri, det står i kontrakten: et tidsintervall-bilde uten
 * folk på er INGEN informasjon, mens et PIR-utløst bilde uten folk på betyr at
 * noe varmt beveget seg og var ute av bildet — altså et mål kameraet IKKE fikk.
 */
export const TOMT_BILDE_BETYR: Record<'pir' | 'tidsintervall' | 'manuell' | 'bevegelse-i-bilde', string> = {
  pir: 'Tomt bilde her betyr at NOE VARMT beveget seg, men var ute av bildeflaten. Det er en observasjon, ikke et blindtreff.',
  tidsintervall: 'Tomt bilde her betyr ingenting — kameraet tok bildet uansett.',
  manuell: 'Tomt bilde her betyr at den som utløste ikke fikk det han ville ha.',
  'bevegelse-i-bilde': 'Tomt bilde her er selvmotsigende: noden mente det var bevegelse i bildeflaten.',
};
